import { 
  Phone, 
  Users, 
  Calendar, 
  BarChart3, 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown, Minus, 
  Activity, Bot, Star, 
  Headphones, Gauge, MessageSquare,
   Mail, Bell
} from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';
import { useNavigate } from 'react-router-dom';
import { useGetUpcomingAppointments } from '../../services/query/appointment/appointment';
import UpComingAppointment from './UpcomingAppointment';
import { UpcomingAppointment } from '../../services/api/appointment/appointmentApi';

export default function Dashboard() {
  const { state } = useAppContext();
  const navigate = useNavigate();
  const { 
    calls, 
    patients, 
    appointments, 
    reminders, 
    operationalMetrics, 
    aiAgentMetrics 
  } = state;

  const todaysCalls = calls.filter(call => {
    const today = new Date().toDateString();
    return call.startTime.toDateString() === today;
  });


  const {
    data: upcomingAppointmentData, 
    isLoading: isLoadingAppointments
  } = useGetUpcomingAppointments({limit: 10, page: 1});

  console.log(upcomingAppointmentData);
  

  const todaysSMS = reminders.filter(r => {
    const today = new Date().toDateString();
    return r.type === 'sms' && new Date(r.scheduledFor).toDateString() === today;
  });

  const todaysEmails = reminders.filter(r => {
    const today = new Date().toDateString();
    return r.type === 'email' && new Date(r.scheduledFor).toDateString() === today;
  });

  const totalContactsToday = todaysCalls.length + todaysSMS.length + todaysEmails.length;

  const avgCallDuration = calls.length > 0
    ? calls.reduce((sum, call) => sum + (call.duration || 0), 0) / calls.length
    : 0;

  const upcomingAppointments = appointments.filter(apt => {
    const today = new Date();
    const aptDate = new Date(apt.date);
    return aptDate >= today && apt.status === 'scheduled';
  }).slice(0, 5);

  const todaysAppointments = appointments.filter(apt => {
    const today = new Date().toDateString();
    return new Date(apt.date).toDateString() === today;
  });

  const pendingReminders = reminders.filter(r => r.status === 'scheduled').length;
  const deliveredReminders = reminders.filter(r => r.status === 'delivered' || r.status === 'sent').length;
  const reminderDeliveryRate = reminders.length > 0 ? Math.round((deliveredReminders / reminders.length) * 100) : 0;

  const stats = [
    { label: 'Total Contacts Today', value: totalContactsToday, icon: Activity, color: 'blue', sublabel: `${todaysCalls.length} calls • ${todaysSMS.length} SMS • ${todaysEmails.length} emails` },
    { label: 'Active Patients', value: patients.length, icon: Users, color: 'green', sublabel: `${patients.filter(p => p.aiCreated).length} AI-created` },
    { label: 'Today\'s Appointments', value: todaysAppointments.length, icon: Calendar, color: 'purple', sublabel: `${upcomingAppointments.length} upcoming total` },
    { label: 'Reminder Delivery', value: `${reminderDeliveryRate}%`, icon: Bell, color: 'orange', sublabel: `${pendingReminders} pending` }
  ];

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down': return <TrendingDown className="w-4 h-4 text-red-500" />;
      default: return <Minus className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Overview</h1>
            <p className="text-blue-100 mt-1">for Estrabillo Dental Group</p>
          </div>
          <div className="text-right">
            <p className="text-green-100">Today's Date</p>
            <p className="text-xl font-semibold">{new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1">
                <p className="text-sm text-gray-600">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-${stat.color}-100`}>
                <stat.icon className={`w-6 h-6 text-${stat.color}-600`} />
              </div>
            </div>
            <p className="text-xs text-gray-500">{stat.sublabel}</p>
          </div>
        ))}
      </div>

      {/* Communication Channels Today */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <MessageSquare className="w-6 h-6 mr-2 text-blue-600" />
          Today's Communication Breakdown
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Phone className="w-5 h-5 text-blue-600" />
                <h4 className="font-semibold text-blue-900">Calls</h4>
              </div>
              <span className="text-2xl font-bold text-blue-900">{todaysCalls.length}</span>
            </div>
            <div className="text-sm text-blue-700">
              <p>{calls.filter(c => c.type === 'inbound').length} inbound • {calls.filter(c => c.type === 'outbound').length} outbound</p>
              <p className="text-xs mt-1">Avg duration: {Math.round(avgCallDuration)}s</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <MessageSquare className="w-5 h-5 text-green-600" />
                <h4 className="font-semibold text-green-900">SMS</h4>
              </div>
              <span className="text-2xl font-bold text-green-900">{todaysSMS.length}</span>
            </div>
            <div className="text-sm text-green-700">
              <p>{reminders.filter(r => r.type === 'sms' && r.status === 'delivered').length} delivered</p>
              <p className="text-xs mt-1">96% success rate</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Mail className="w-5 h-5 text-purple-600" />
                <h4 className="font-semibold text-purple-900">Emails</h4>
              </div>
              <span className="text-2xl font-bold text-purple-900">{todaysEmails.length}</span>
            </div>
            <div className="text-sm text-purple-700">
              <p>{reminders.filter(r => r.type === 'email' && r.status === 'delivered').length} delivered</p>
              <p className="text-xs mt-1">89% success rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Operational Insights */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900 flex items-center">
            <Activity className="w-6 h-6 mr-2 text-blue-600" />
            Operational Insights
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* Operational Health Gauge */}
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-emerald-900">Health Gauge</h4>
              <Gauge className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-900">{operationalMetrics.operationalHealthGauge}</p>
              <p className="text-sm text-emerald-700 mt-1">Overall score</p>
              <div className="w-full bg-emerald-200 rounded-full h-2 mt-2">
                <div 
                  className="bg-emerald-600 h-2 rounded-full transition-all duration-500" 
                  style={{ width: `${operationalMetrics.operationalHealthGauge}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* No-Show Rate */}
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-red-900">No-Show Rate</h4>
              <div className="flex items-center space-x-1">
                {getTrendIcon(operationalMetrics.noShowRate.trend)}
              </div>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-900">{operationalMetrics.noShowRate.current}%</p>
              <p className="text-sm text-red-700 mt-1">Target: {operationalMetrics.noShowRate.target}%</p>
            </div>
          </div>

          {/* Patient Retention */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-blue-900">Retention Rate</h4>
              <div className="flex items-center space-x-1">
                {getTrendIcon(operationalMetrics.patientRetentionRate.trend)}
              </div>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-900">{operationalMetrics.patientRetentionRate.current}%</p>
              <p className="text-sm text-blue-700 mt-1">Target: {operationalMetrics.patientRetentionRate.target}%</p>
            </div>
          </div>

          {/* Uptime Insights */}
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-yellow-900">Uptime Insights</h4>
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-900">{operationalMetrics.uptimeMaximizerInsights.length}</p>
              <p className="text-sm text-yellow-700 mt-1">Action items</p>
            </div>
            <div className="mt-2 space-y-1">
              {operationalMetrics.uptimeMaximizerInsights.slice(0, 2).map((insight, index) => (
                <div key={index} className="text-xs text-yellow-800 bg-yellow-200 rounded px-2 py-1">
                  {insight.patientName}: {insight.type.replace('_', ' ')}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Chair Utilization Heatmap */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-4">Chair Utilization Heatmap</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {operationalMetrics.chairUtilizationData.map((chair) => (
              <div key={chair.chairId} className="bg-white rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-gray-900">{chair.chairName}</h5>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    chair.efficiency === 'high' ? 'bg-green-100 text-green-800' :
                    chair.efficiency === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {chair.efficiency}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">{chair.provider}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">Utilization:</span>
                  <span className="font-semibold text-gray-900">{chair.utilizationRate}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-500 ${
                      chair.utilizationRate >= 90 ? 'bg-green-500' :
                      chair.utilizationRate >= 70 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${chair.utilizationRate}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {chair.hoursBooked}h / {chair.totalHours}h booked
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI Agent Performance */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900 flex items-center">
            <Bot className="w-6 h-6 mr-2 text-purple-600" />
            AI Agent Performance
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* Appointments Booked by AI */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-purple-900">AI Bookings</h4>
              <div className="flex items-center space-x-1">
                {getTrendIcon(aiAgentMetrics.appointmentsBookedByAI.trend)}
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-900">{aiAgentMetrics.appointmentsBookedByAI.today}</p>
                <p className="text-xs text-purple-700">Today</p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-center">
                  <p className="font-semibold text-purple-800">{aiAgentMetrics.appointmentsBookedByAI.thisWeek}</p>
                  <p className="text-xs text-purple-600">This Week</p>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-purple-800">{aiAgentMetrics.appointmentsBookedByAI.thisMonth}</p>
                  <p className="text-xs text-purple-600">This Month</p>
                </div>
              </div>
            </div>
          </div>

          {/* Call Resolution Rate */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-green-900">Resolution Rate</h4>
              <div className="flex items-center space-x-1">
                {getTrendIcon(aiAgentMetrics.callResolutionRate.trend)}
              </div>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-900">{aiAgentMetrics.callResolutionRate.rate}%</p>
              <p className="text-sm text-green-700 mt-1">Target: {aiAgentMetrics.callResolutionRate.target}%</p>
              <div className="w-full bg-green-200 rounded-full h-2 mt-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all duration-500" 
                  style={{ width: `${aiAgentMetrics.callResolutionRate.rate}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Patient Satisfaction */}
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-yellow-900">Satisfaction</h4>
              <div className="flex items-center space-x-1">
                <Star className="w-4 h-4 text-yellow-600 fill-current" />
                {getTrendIcon(aiAgentMetrics.patientSatisfactionScore.trend)}
              </div>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-900">{aiAgentMetrics.patientSatisfactionScore.score}</p>
              <p className="text-sm text-yellow-700 mt-1">out of 5.0</p>
              <div className="flex justify-center mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star} 
                    className={`w-4 h-4 ${
                      star <= Math.floor(aiAgentMetrics.patientSatisfactionScore.score) 
                        ? 'text-yellow-500 fill-current' 
                        : 'text-yellow-300'
                    }`} 
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Seamless Handoff Status */}
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-indigo-900">Handoff Queue</h4>
              <Headphones className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="space-y-2">
              <div className="text-center">
                <p className="text-2xl font-bold text-indigo-900">{aiAgentMetrics.seamlessHandoffStatus.queueLength}</p>
                <p className="text-xs text-indigo-700">In queue</p>
              </div>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-indigo-700">Avg Wait:</span>
                  <span className="font-medium text-indigo-900">{aiAgentMetrics.seamlessHandoffStatus.avgWaitTime}s</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-indigo-700">Escalation:</span>
                  <span className="font-medium text-indigo-900">{aiAgentMetrics.seamlessHandoffStatus.escalationRate}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Patient Feedback */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-4">Recent Patient Feedback</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {aiAgentMetrics.patientSatisfactionScore.recentFeedback.map((feedback, index) => (
              <div key={index} className="bg-white rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-gray-900">{feedback.patientName}</h5>
                  <div className="flex items-center space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star} 
                        className={`w-4 h-4 ${
                          star <= feedback.rating 
                            ? 'text-yellow-500 fill-current' 
                            : 'text-gray-300'
                        }`} 
                      />
                    ))}
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-2">"{feedback.comment}"</p>
                <p className="text-xs text-gray-500">{feedback.date}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Active Handoffs */}
        {aiAgentMetrics.seamlessHandoffStatus.activeHandoffs.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-gray-900 mb-4">Active Handoffs</h4>
            <div className="space-y-3">
              {aiAgentMetrics.seamlessHandoffStatus.activeHandoffs.map((handoff, index) => (
                <div key={index} className="bg-white rounded-lg p-4 border flex items-center justify-between">
                  <div>
                    <h5 className="font-medium text-gray-900">{handoff.patientName}</h5>
                    <p className="text-sm text-gray-600">{handoff.reason}</p>
                    {handoff.assignedAgent && (
                      <p className="text-xs text-gray-500">Assigned to: {handoff.assignedAgent}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      handoff.priority === 'high' ? 'bg-red-100 text-red-800' :
                      handoff.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {handoff.priority}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">Wait: {Math.floor(handoff.waitTime / 60)}m {handoff.waitTime % 60}s</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/patients')}
              className="w-full p-4 text-left bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Users className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-semibold text-green-900">View All Patients</p>
                  <p className="text-sm text-green-700">Browse patient directory & manage profiles</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/appointments')}
              className="w-full p-4 text-left bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Calendar className="w-6 h-6 text-purple-600" />
                <div>
                  <p className="font-semibold text-purple-900">Manage Appointments</p>
                  <p className="text-sm text-purple-700">Schedule & modify appointments</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/reminders')}
              className="w-full p-4 text-left bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Bell className="w-6 h-6 text-orange-600" />
                <div>
                  <p className="font-semibold text-orange-900">Reminders & Notifications</p>
                  <p className="text-sm text-orange-700">Manage patient communications</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/analytics')}
              className="w-full p-4 text-left bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <BarChart3 className="w-6 h-6 text-blue-600" />
                <div>
                  <p className="font-semibold text-blue-900">View Analytics</p>
                  <p className="text-sm text-blue-700">Insights & reports dashboard</p>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {calls.slice(-5).reverse().map((call) => (
              <div key={call.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className={`w-2 h-2 rounded-full ${
                  call.urgencyLevel === 'emergency' ? 'bg-red-500' :
                  call.urgencyLevel === 'high' ? 'bg-orange-500' :
                  call.urgencyLevel === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                }`}></div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{call.patientName}</p>
                  <p className="text-sm text-gray-600">
                    {call.type} call • {call.purpose} • {call.startTime.toLocaleTimeString()}
                  </p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  call.status === 'connected' ? 'bg-green-100 text-green-800' :
                  call.status === 'ended' ? 'bg-gray-100 text-gray-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {call.status}
                </span>
              </div>
            ))}
            
            {calls.length === 0 && (
              <div className="text-center text-gray-500 py-8">
                <Phone className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No recent calls</p>
                <p className="text-sm">Start a call to see activity here</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upcoming Appointments */}
      <UpComingAppointment 
      upcomingAppointments={
        upcomingAppointmentData?.data as unknown as UpcomingAppointment[]
      }
      isLoading={isLoadingAppointments} />
    </div>
  );
}